import React from "react";

/* ---------------- CHAPTER ROW ---------------- */

const ChapterRow = ({ chapter }) => {
  return (
    <div className="flex items-center justify-between text-[11px] py-[2px] border-b last:border-0">
      {/* Left: progress + name */}
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-10 h-1 bg-gray-200 rounded">
          <div
            className="h-1 bg-blue-500 rounded"
            style={{ width: `${chapter.progress || 0}%` }}
          />
        </div>

        <span className="truncate max-w-[120px]">
          {chapter.name || chapter.id}
        </span>
      </div>

      {/* Right: compact status */}
      <div className="flex items-center gap-[2px]">
        <span>{chapter.theory?.status ? "✔" : "✖"}</span>
        <span>{chapter.practice?.status ? "✔" : "✖"}</span>
        <span>{chapter.revision?.status ? "✔" : "✖"}</span>

        {chapter.strength && (
          <span
            className={`ml-1 w-2 h-2 rounded-full ${
              chapter.strength === "strong" ? "bg-green-500" : "bg-red-500"
            }`}
          />
        )}
      </div>
    </div>
  );
};

/* ---------------- SECTION (NO COLLAPSE) ---------------- */

const Section = ({ name, chapters }) => {
  return (
    <div className="mt-2">
      <div className="text-[10px] font-semibold text-gray-500 mb-1 border-t pt-1">
        {name}
      </div>

      <div>
        {chapters.map((ch) => (
          <ChapterRow key={ch.id} chapter={ch} />
        ))}
      </div>
    </div>
  );
};

/* ---------------- SUBJECT (COLUMN) ---------------- */

const Subject = ({ subject }) => {
  const totalChapters = subject.sections.reduce(
    (acc, sec) => acc + sec.chapters.length,
    0,
  );

  const completedChapters = subject.sections.reduce(
    (acc, sec) => acc + sec.chapters.filter((c) => c.progress === 100).length,
    0,
  );

  return (
    <div className="min-w-[220px] max-w-[260px] border rounded-lg bg-white flex-shrink-0">
      {/* Header */}
      <div className="text-xs font-semibold px-2 py-1 border-b bg-gray-50 sticky top-0 z-10">
        <div className="flex justify-between">
          <span className="truncate">{subject.name}</span>
          <span className="text-gray-500">
            {completedChapters}/{totalChapters}
          </span>
        </div>
      </div>

      {/* Sections */}
      <div className="px-2 pb-2">
        {subject.sections.map((section) => (
          <Section
            key={section.name}
            name={section.name}
            chapters={section.chapters}
          />
        ))}
      </div>
    </div>
  );
};

/* ---------------- PAPER ---------------- */

const Paper = ({ paper }) => {
  return (
    <div className="border rounded-xl bg-white">
      {/* Header */}
      <div className="flex justify-between items-center p-3 border-b">
        <h1 className="text-sm font-semibold">{paper.name}</h1>

        <div className="flex items-center gap-2">
          <div className="w-20 h-1 bg-gray-200 rounded">
            <div
              className="h-1 bg-green-500 rounded"
              style={{ width: `${paper.progress || 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Horizontal Subjects */}
      <div className="px-3 py-2 overflow-x-auto">
        <div className="flex gap-3 min-w-max">
          {paper.subjects.map((sub) => (
            <Subject key={sub.name} subject={sub} />
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---------------- MAIN VIEW ---------------- */

export default function SyllabusHierarchyViewer({ progress }) {
  const [data, setData] = React.useState([]);

  React.useEffect(() => {
    if (!progress) return;

    const viewData = buildHierarchy(
      progress?.syllabus?.papers || [],
      progress?.progress || {},
      progress?.chapters || [],
    );

    setData(viewData);
  }, [progress]);

  return (
    <div className="p-3 space-y-3">
      {data.map((paper) => (
        <Paper key={paper.name} paper={paper} />
      ))}
    </div>
  );
}

/* ---------------- DATA TRANSFORM UTIL ---------------- */

export function buildHierarchy(papers, progress, chapters = []) {
  const result = [];

  papers.forEach((paper) => {
    const paperNode = {
      name: paper.name,
      progress: progress.progress?.paperProgress?.[paper.name] || 0,
      subjects: [],
    };

    paper.subjects.forEach((sub) => {
      const subjectNode = {
        name: sub.id,
        sections: [],
      };

      const subjectChapters = chapters
        .filter((ch) => ch.subjectId === sub.id)
        .map((meta) => {
          const prog = progress.progress?.chapters?.[meta.id] || {};

          const derivedProgress =
            prog.progress ??
            (prog.completed?.status
              ? 100
              : [
                  prog.theory?.status,
                  prog.practice?.status,
                  prog.revision?.status,
                ].filter(Boolean).length * 33.33) ??
            0;

          return {
            id: meta.id,
            name: meta.name,
            subject: meta.subjectId,
            section: meta.section || "General",
            progress: Math.round(derivedProgress),
            theory: prog.theory || { status: false },
            practice: prog.practice || { status: false },
            revision: prog.revision || { status: false },
            strength: prog.strength,
          };
        });

      const sectionMap = {};

      subjectChapters.forEach((ch) => {
        const sectionName = ch.section || "General";
        if (!sectionMap[sectionName]) sectionMap[sectionName] = [];
        sectionMap[sectionName].push(ch);
      });

      subjectNode.sections = Object.entries(sectionMap).map(
        ([name, chapters]) => ({
          name,
          chapters,
        }),
      );

      paperNode.subjects.push(subjectNode);
    });

    result.push(paperNode);
  });

  return result;
}
